import React, { useEffect, useState } from 'react';
import SortableTree, { changeNodeAtPath, removeNodeAtPath } from '@nosferatu500/react-sortable-tree';
import '@nosferatu500/react-sortable-tree/style.css';
import {
  Flex,
  Box,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Checkbox,
  Select,
  Input,
  Spinner
} from '@chakra-ui/react';
import { MdOutlineDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import SettingsBreadcrumb from '../../../components/SettingsBreadcrumb';
import Swal from 'sweetalert2';
import * as api from '../../../api/faixaNavegacao';
import './faixa-navegacao.scss';

const FaixaNavegacaoPrincipal: React.FC = () => {
  const navigate = useNavigate();

  const [treeData, setTreeData] = useState<any[]>([]);
  const [paginasAtivas, setPaginasAtivas] = useState<any[]>([]);
  const [dadosPagina, setDadosPagina] = useState<any[]>([]);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  useEffect(() => {
    fetchPaginasAtivas();
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      const menus = await api.listarFaixaNavegacao();
      const formattedMenus = menusFormatados(menus);
      setTreeData(formattedMenus);
    } catch (err) {
      console.error("Erro ao buscar menus:", err);
    }
  };

  const fetchPaginasAtivas = async () => {
    try {
      const data = await api.paginasAtivas();
      setPaginasAtivas(data);
      setDadosPagina(data);
    } catch (error) {
      console.error('Erro ao buscar páginas ativas:', error);
    }
  };

  const menusFormatados = (menus: any[]): any[] => {
    const map = new Map<number, any>();
    const res: any[] = [];

    menus.forEach((menu) => {
      const item = {
        ID: menu.seqFaixaNavegacao,
        Nome: menu.dsctituloMenu,
        pagina: menu.pagina ? menu.pagina.seqPagina : null,
        ativo: menu.numVisivel === 1,
        parentID: menu.fkSeqFaixaNavegacao ? menu.fkSeqFaixaNavegacao : null,
        children: []
      };
      map.set(menu.seqFaixaNavegacao, item);
    });

    menus.forEach((menu) => {
      const item = map.get(menu.seqFaixaNavegacao);
      if (menu.fkSeqFaixaNavegacao) {
        const parent = map.get(menu.fkSeqFaixaNavegacao);
        if (parent) {
          parent.children.push(item);
        }
      } else {
        res.push(item);
      }
    });

    return res;
  };

  const handleCheckboxChange = (node: any, path: number[]) => {
    const newTreeData = changeNodeAtPath({
      treeData,
      path,
      getNodeKey: ({ node: n }: any) => n.ID,
      newNode: { ...node, ativo: !node.ativo }
    });
    setTreeData(newTreeData);
  };

  const handlePageChange = (node: any, path: number[], newPagina: string) => {
    const newTreeData = changeNodeAtPath({
      treeData,
      path,
      getNodeKey: ({ node: n }: any) => n.ID,
      newNode: { ...node, pagina: newPagina }
    });
    setTreeData(newTreeData);
  };

  const handleNomeDoubleClick = (node: any) => {
    setEditingNodeId(node.ID);
    setEditingValue(node.Nome);
  };

  const handleNomeBlur = (node: any, path: number[]) => {
    const newTreeData = changeNodeAtPath({
      treeData,
      path,
      getNodeKey: ({ node: n }: any) => n.ID,
      newNode: { ...node, Nome: editingValue }
    });
    setTreeData(newTreeData);
    setEditingNodeId(null);
  };

  const handleAddItem = () => {
    const newNode = {
      ID: Date.now(),
      Nome: 'Novo item',
      pagina: null,
      ativo: true,
      isNew: true,
      children: []
    };

    setTreeData((prevTreeData) => [newNode, ...prevTreeData]);
  };

  const handleDelete = (node: any, path: number[]) => {
    const temSubItem = Array.isArray(node.children) && node.children.length > 0

    const opcoesMensagem = {
      title: 'Tem certeza?',
      text: temSubItem
        ? 'Certeza que deseja deletar este item? Ele será deletado junto com todos os seus subItems'
        : 'Certeza que deseja deletar este item?',
      icon: 'warning' as const,
      showCancelButton: true,
      confirmButtonColor: '#1789FC',
      cancelButtonColor: '#EDF2F7',
      confirmButtonText: temSubItem ? 'Sim, deletar todos' : 'Sim, deletar',
      cancelButtonText: 'Cancelar'
    }

    Swal.fire(opcoesMensagem).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'info',
          title: 'Aguarde',
          allowOutsideClick: false,
          text: 'Deletando...',
          showConfirmButton: false
        })
        try {
          await api.deletarFaixaDeNavegacao(node.ID)

          setTreeData((currentTree) =>
            removeNodeAtPath({
              treeData: currentTree,
              path,
              getNodeKey: ({ node: n }: any) => n.ID
            })
          )

          await Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Item deletado com sucesso.',
            confirmButtonColor: '#1789FC',
            confirmButtonText: 'OK'
          })
        } catch (error: any) {
          console.error('Erro ao deletar:', error)
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.message || 'Não foi possível deletar o item.',
            confirmButtonColor: '#1789FC',
            confirmButtonText: 'OK'
          })
        }
      }
    })
  }

  const handleSave = async () => {
    let ordem = 1;
    
    const traverseMenu = (nodes: any[], parent: any = null): any[] => {
      let itensTree: any[] = [];
      
      nodes.forEach((node: any) => {
        const seqPagina =
          typeof node.pagina === 'string'
            ? Number(node.pagina)
            : node.pagina;
        const infoPagina = dadosPagina.find(
          (p: any) => p.seqPagina === seqPagina
        );
  
        const newItem = {
          seqFaixaNavegacao : node.ID.toString().length >= 10 ? null : node.ID,
          fkSeqFaixaNavegacao: parent ? parent.seqFaixaNavegacao : null,
          dsctituloMenu: node.Nome,
          pagina: {
            seqPagina: seqPagina
          },
          dscUrl: infoPagina ? infoPagina.dscUrl : null,
          numOrdem: ordem++,
          numLocal: 0,
          numVisivel: node.ativo ? 1 : 0,
          subMenu: node.children && node.children.length > 0 ? 1 : 0,
        };
  
        itensTree.push(newItem);
  
        if (node.children && node.children.length > 0) {
          itensTree = itensTree.concat(traverseMenu(node.children, newItem));
        }
      });
      return itensTree;
    };
  
    const updatedMenuData = traverseMenu(treeData);
  
    const ordemInvalida = updatedMenuData.some(item => item.numOrdem > 99);
    if (ordemInvalida) {
      Swal.fire({
        icon: 'warning',
        title: 'Alerta!',
        text: 'O número de itens não pode passar de 99.',
        confirmButtonColor: '#1789FC',
        confirmButtonText: 'OK',
      });
      return;
    }
  
    try {
      await api.editarFaixaNavegacao(updatedMenuData);
      await fetchMenus();
      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Faixa de navegação alterada com sucesso.',
        confirmButtonColor: '#1789FC',
        confirmButtonText: 'OK',
      }).then(() => {
        navigate('/cms/edicao-cidade-inteligente');
      });
    } catch (error) {
      console.error('Erro ao alterar faixa de navegação:', error);
      Swal.fire({
        icon: 'error',
        title: 'Ocorreu um erro!',
        text: 'Erro ao alterar faixa de navegação. Tente novamente mais tarde.',
        confirmButtonColor: '#1789FC',
        confirmButtonText: 'OK',
      });
    }
  };  

  return (
    <Flex p="40px" bgColor="#fafafa" direction="column" w="100%">
      <SettingsBreadcrumb />
      <Heading
        mt="30px"
        mb="20px"
        fontWeight="600"
        fontSize="21px"
        color="#6F6F6F"
        w="100%"
      >
        Gerenciar Faixa de Navegação Principal
      </Heading>
      {!paginasAtivas || paginasAtivas.length === 0 ? (
        <Flex
          justify="center"
          align="center"
          direction="column"
          w="70%"
          h="90%"
          position='fixed'
        >
          <Spinner size="xl" color="blue.500" />
        </Flex>
      ) : (
        <Box m="30px 0" w="100%">

          <Box mb="20px">
            <Button colorScheme="blue" onClick={handleAddItem}>
              Adicionar Item
            </Button>
          </Box>

          <Box height="500px" overflowX="hidden" overflowY="hidden">
            <Flex
              overflowY="hidden"
              position="sticky"
              overflowX="hidden"
              top={0}
              zIndex={2}
              bg="gray.200"
              py={2}
              px={4}
              fontWeight="bold"
              fontSize="16px"
              display="grid"
              gridTemplateColumns={["1fr 1fr 1fr 1fr", "2fr 1fr 1fr 1fr"]}
              gap={2}
              borderBottom="1px solid #ccc"
            >
              <Box ml={"80px"}>Título</Box>
              <Box ml={"40px"}>Página</Box>
              <Box ml={"150px"}>Ativo</Box>
              <Box ml={"5px"}>Deletar</Box>
            </Flex>

            <SortableTree
              treeData={treeData}
              onChange={(newTreeData: any) => setTreeData(newTreeData)}
              getNodeKey={({ node }: any) => node.ID}
              generateNodeProps={({ node, path }: any) => {
                return {
                  title: (
                    <Flex
                      className="linha-tree"
                      display="grid"
                      gridTemplateColumns={["1fr 1fr 1fr 1fr", "2fr 1fr 1fr 1fr"]}
                      borderBottom="1px solid #ddd"
                      alignItems="center"
                      py={2}
                      px={2}
                    >
                      <Box className='input-edicao' onDoubleClick={() => handleNomeDoubleClick(node)}>
                        {editingNodeId === node.ID ? (
                          <Input
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => handleNomeBlur(node, path)}
                            autoFocus
                            size="sm"
                          />
                        ) : (
                          node.Nome
                        )}
                      </Box>
                      <Box>
                        <Select
                          width={'180px'}
                          placeholder="Selecione a página"
                          value={node.pagina || ''}
                          onChange={(e) => handlePageChange(node, path, e.target.value)}
                          size="sm"
                        >
                          {dadosPagina.map((pg: any) => (
                            <option key={pg.seqPagina} value={pg.seqPagina}>
                              {pg.nomPagina}
                            </option>
                          ))}
                        </Select>
                      </Box>
                      <Box ml={40}>
                        <Checkbox
                          mt={'5px'}
                          isChecked={node.ativo}
                          onChange={() => handleCheckboxChange(node, path)}
                        />
                      </Box>
                      <Box ml={"30px"}>
                        <Button
                          variant="ghost"
                          size="sm"
                          colorScheme="red"
                          _hover={{ bg: '#ff00002b' }}
                          onClick={() => handleDelete(node, path)}
                        >
                          <MdOutlineDelete size={24} color ='red' />
                        </Button>
                      </Box>
                    </Flex>
                  ),
                };
              }}
              {...({ isVirtualized: false } as any)}
            />
          </Box>

          <FormControl mt="24px">
            <FormLabel fontWeight={'bold'}>Preview da Faixa de Navegação</FormLabel>
          </FormControl>

          {treeData.length > 0 && (
            <nav
              className="navbar navbar-expand-lg navbar-light"
              style={{
                width: '100%',
                maxWidth: '100vw',
                whiteSpace: 'nowrap',
                backgroundColor: '#ebebeb',
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              <div
                className="container-fluid"
                style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}
              >
                <div className="collapse navbar-collapse" style={{ width: '100%' }}>
                  <ul
                    className="navbar-nav mr-auto"
                    style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}
                  >
                    {treeData
                      .filter((item) => item.ativo)
                      .map((item) => (
                        <li
                          className={`nav-item ${item.children && item.children.length > 0 ? 'dropdown' : ''
                            } ${openDropdown === item.ID ? 'show' : ''}`}
                          key={item.ID}
                          onMouseEnter={() => setOpenDropdown(item.ID)}
                          onMouseLeave={() => setOpenDropdown(null)}
                        >
                          {item.children && item.children.length > 0 ? (
                            <>
                              <button className="btn dropdown-toggle" role="button">
                                {item.Nome}
                              </button>
                              <ul
                                className={`dropdown-menu ${openDropdown === item.ID ? 'show' : ''
                                  }`}
                              >
                                {item.children
                                  .filter((sub: any) => sub.ativo)
                                  .map((sub: any) => (
                                    <li key={sub.ID}>
                                      <a
                                        className="dropdown-item"
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {sub.Nome}
                                      </a>
                                    </li>
                                  ))}
                              </ul>
                            </>
                          ) : (
                            <a
                              className="nav-link"
                              style={{ cursor: 'pointer' }}
                            >
                              {item.Nome}
                            </a>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </nav>
          )}
          <Flex w="100%" justifyContent="flex-end" mt="20px">
            <Button
              type="submit"
              m="6px"
              color="#FFF"
              bgColor="#1665D8"
              _hover={{ opacity: 0.8 }}
              onClick={handleSave}
              disabled={isEditing}
            >
              Salvar Faixa de Navegação
            </Button>
          </Flex>
        </Box>
      )}
    </Flex>
  );
};

export default FaixaNavegacaoPrincipal;
